import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Clock, CheckCircle, XCircle } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';

const STATUS_ICON = { pending: Clock, approved: CheckCircle, rejected: XCircle };
const STATUS_COLOR = { pending: 'text-yellow-500', approved: 'text-green-500', rejected: 'text-red-500' };

export default function UserPanel() {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [form, setForm] = useState({ type: 'content', data: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/submissions').then(res => setSubmissions(res.data.submissions)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let parsedData;
      try { parsedData = JSON.parse(form.data); } catch { parsedData = { note: form.data }; }
      const res = await api.post('/submissions', { type: form.type, data: parsedData });
      setSubmissions([res.data.submission, ...submissions]);
      setForm({ type: 'content', data: '' });
      toast.success('Submission sent for review.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="My Dashboard" />
      <div className="pt-24 pb-16 px-6 max-w-4xl mx-auto">
        <h1 className="font-serif text-3xl text-ink mb-2">Welcome, {user?.name}</h1>
        <p className="text-ink-400 text-sm mb-10">Submit content suggestions for admin review.</p>

        {/* Submit form */}
        <div className="bg-white border border-dark-600 p-8 mb-10">
          <h2 className="font-serif text-xl text-ink mb-6">New Submission</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs tracking-widest uppercase text-ink-400 mb-2">Type</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                className="w-full bg-white border border-dark-600 text-ink px-4 py-3 text-sm focus:outline-none focus:border-gold">
                <option value="content">Content</option>
                <option value="product">Product</option>
                <option value="collection">Collection</option>
                <option value="solution">Solution</option>
              </select>
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase text-ink-400 mb-2">Details / Notes</label>
              <textarea rows={4} required value={form.data} onChange={e => setForm({ ...form, data: e.target.value })}
                placeholder="Describe your suggestion or paste JSON data..."
                className="w-full bg-white border border-dark-600 text-ink px-4 py-3 text-sm focus:outline-none focus:border-gold resize-none" />
            </div>
            <button type="submit" disabled={loading} className="btn-gold disabled:opacity-50">
              {loading ? 'Submitting...' : 'Submit for Review'}
            </button>
          </form>
        </div>

        {/* Submissions list */}
        <h2 className="font-serif text-xl text-ink mb-6">My Submissions</h2>
        {submissions.length === 0 ? (
          <p className="text-ink-400 text-sm">No submissions yet.</p>
        ) : (
          <div className="space-y-4">
            {submissions.map(sub => {
              const Icon = STATUS_ICON[sub.status];
              return (
                <div key={sub._id} className="bg-white border border-dark-600 p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs tracking-widest uppercase text-gold">{sub.type}</span>
                    <div className={`flex items-center gap-1 text-xs ${STATUS_COLOR[sub.status]}`}>
                      <Icon size={14} /> {sub.status}
                    </div>
                  </div>
                  <p className="text-ink-400 text-sm">{typeof sub.data === 'string' ? sub.data : JSON.stringify(sub.data)}</p>
                  {sub.adminNote && <p className="text-ink-400 text-xs mt-2 italic">Admin note: {sub.adminNote}</p>}
                  <p className="text-gray-600 text-xs mt-2">{new Date(sub.createdAt).toLocaleDateString()}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
